import { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  // Optional footer with action buttons; rendered as a right-aligned row.
  footer?: React.ReactNode;
  // Set true for narrow prompts (e.g., edit message dialog).
  size?: 'sm' | 'md' | 'lg';
  // Optional CSS selector for the element to focus when the modal opens.
  // Defaults to the first focusable (which is normally the close button),
  // but for dialogs that want the user to start typing immediately
  // (e.g. an edit textarea), pass `initialFocusSelector='textarea[data-autofocus]'`.
  initialFocusSelector?: string;
}

const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Modal({ open, onClose, title, children, footer, size = 'md', initialFocusSelector }: ModalProps) {
  const titleId = useRef(`cf-modal-title-${Math.random().toString(36).slice(2, 9)}`);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const focusFirst = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;
    // Prefer a caller-specified target (e.g. the edit textarea) over the
    // generic first-focusable, so the user can start typing immediately
    // when a dialog expects input.
    if (initialFocusSelector) {
      const target = panel.querySelector<HTMLElement>(initialFocusSelector);
      if (target) {
        target.focus();
        return;
      }
    }
    const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const first = focusables[0] ?? panel;
    first.focus();
  }, [initialFocusSelector]);

  useEffect(() => {
    if (!open) return;

    // Remember the element that had focus before opening so we can restore
    // it on close. Without this, focus would fall back to <body> and the
    // user would lose their place (e.g. on the button that triggered the
    // modal).
    previouslyFocused.current =
      (document.activeElement as HTMLElement | null) ?? null;

    // Lock body scroll while the modal is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the first focusable element on the next tick so the panel has
    // been mounted.
    const id = window.setTimeout(focusFirst, 0);

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      // Restore focus to whatever was focused before the modal opened.
      const prev = previouslyFocused.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [open, onClose, focusFirst]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        // Only close when the backdrop itself is clicked; clicks inside the
        // panel (or its portal-bubbled children) must NOT dismiss.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId.current}
        className={clsx(
          'card flex w-full flex-col',
          SIZE_CLASSES[size],
          'max-h-[90vh]',
        )}
      >
        <div className="flex items-center justify-between border-b border-ink-100 px-4 py-3 dark:border-ink-700">
          <h2 id={titleId.current} className="text-lg font-semibold">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            className="btn-ghost px-2"
            onClick={onClose}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="nice-scroll flex-1 overflow-y-auto p-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-ink-100 px-4 py-3 dark:border-ink-700">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}